import React, { useCallback } from "react";
import { sortPermissions } from "./helpers";
import { accessService, rolePermissionService } from "/app/src/services";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { buildParams } from "/app/src/helpers";
import PermissionSwitch from "./permissionSwitch";
import { Access, Permission } from "/app/src/models";
import { useTranslation } from "react-i18next";

/**
 * Displays the permission level for a given role for the given resource
 * @param param0 props containing the role id, the code of the permission, the resource name and the list of all permissions
 */
export default function PermissionLevel({
  code,
  roleId,
  resource,
  allPermissions,
  view = true,
  edit = true,
  create = true,
  delete: del = true,
}: {
  code: string;
  roleId: number;
  accessEnabled?: boolean;
  resource: string;
  allPermissions: { permissions: Permission[] };
  view?: boolean;
  edit?: boolean;
  create?: boolean;
  delete?: boolean;
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ["permissions", roleId, code],
    queryFn: () =>
      rolePermissionService
        .getRolePermissions(
          roleId,
          buildParams({
            search: code,
            roleId,
          }),
        )
        .then((response) => {
          return sortPermissions(response.permissions, code);
        }),
    enabled: roleId !== undefined,
    // Ensure shape is always present to avoid undefined access
    initialData: { [code]: [] as string[] },
    refetchOnWindowFocus: false,
  });

  // Memoized helper to determine if a given permission level is checked
  const isPermissionChecked = useCallback(
    (level: "001" | "002" | "003" | "004") => {
      const list: string[] | undefined = data?.[code];
      const checked = Boolean(list?.includes(level));
      // Respect the view toggle visibility when evaluating level 001
      if (level === "001") return checked && view;
      return checked;
    },
    [data, code, view],
  );

  const handleSwitchChange = useCallback(
    async (checked: boolean, level) => {
      const fullCode = `${code}-${level}`;
      const permission = allPermissions.permissions.find(
        (permission) => permission.code === fullCode,
      );
      // Cancel any in-flight permission refetch to prevent overwrite flicker
      await queryClient.cancelQueries({
        queryKey: ["permissions", roleId, code],
      });

      if (!checked) {
        // Optimistic remove
        queryClient.setQueryData(["permissions", roleId, code], (oldData) => {
          if (level === "001") {
            return { [code]: [] };
          }
          const prev: string[] = oldData?.[code] ?? [];
          return { [code]: prev.filter((p) => p !== level) };
        });

        rolePermissionService
          .removeRolePermission(roleId, permission.id)
          .then(() => {
            // Refetch after a short delay to avoid eventual-consistency flicker
            setTimeout(() => {
              queryClient.refetchQueries({
                queryKey: ["permissions", roleId, code],
                type: "active",
              });
            }, 400);
          });
      } else {
        // Optimistic add
        queryClient.setQueryData(["permissions", roleId, code], (oldData) => {
          const prev: string[] = oldData?.[code] ?? [];
          if (prev.includes(level)) return { [code]: prev };
          return { [code]: [...prev, level] };
        });

        rolePermissionService
          .addRolePermission(roleId, permission.id)
          .then(() => {
            // Refetch after a short delay to avoid eventual-consistency flicker
            setTimeout(() => {
              queryClient.refetchQueries({
                queryKey: ["permissions", roleId, code],
                type: "active",
              });
            }, 400);
          });
      }
      //create permissions not available for accesses
      if (level === "002") {
        return;
      }
      //update the access for this resourceType
      const resourceAccesses: { accesses: Access[] } = queryClient.getQueryData(
        ["modifiedResources", roleId, resource],
      );
      if (!resourceAccesses) return;
      const levels = { "001": "view", "003": "edit", "004": "delete" };
      for (const access of resourceAccesses.accesses) {
        let actionList = [];
        if (!checked) {
          if (level === "001") {
            actionList = [];
          } else {
            actionList = access.actionList.filter(
              (action) => action !== levels[level],
            );
          }
        } else {
          if (!access.actionList.includes(levels[level])) {
            access.actionList.push(levels[level]);
          }
          actionList = access.actionList;
        }
        //don't update "fake" accesses - accesses that don't belong to role
        if (access.id) {
          await accessService.updateSingle(access.id, {
            action: actionList.join(","),
          });
        }
      }
      queryClient.invalidateQueries(["modifiedResources", roleId, resource]);
    },
    [allPermissions, code, queryClient, resource, roleId],
  );

  const handleViewSwitchChange = useCallback(
    async (checked: boolean) => {
      return await handleSwitchChange(checked, "001");
    },
    [handleSwitchChange],
  );

  const handleCreateSwitchChange = useCallback(
    async (checked: boolean) => {
      return await handleSwitchChange(checked, "002");
    },
    [handleSwitchChange],
  );

  const handleEditSwitchChange = useCallback(
    async (checked: boolean) => {
      return await handleSwitchChange(checked, "003");
    },
    [handleSwitchChange],
  );

  const handleDeleteSwitchChange = useCallback(
    async (checked: boolean) => {
      return await handleSwitchChange(checked, "004");
    },
    [handleSwitchChange],
  );

  return (
    <>
      {view ? (
        <div>
          <h3>{t("translation:view")}</h3>
          <PermissionSwitch
            isChecked={isPermissionChecked("001")}
            handleSwitchChange={handleViewSwitchChange}
            disabled={false}
          />{" "}
        </div>
      ) : (
        <div />
      )}
      {create ? (
        <div>
          <h3>{t("translation:create")}</h3>
          <PermissionSwitch
            disabled={!data?.[code]?.includes("001") && view}
            isChecked={isPermissionChecked("002")}
            handleSwitchChange={handleCreateSwitchChange}
          />
        </div>
      ) : (
        <div />
      )}
      {edit ? (
        <div>
          <h3>{t("translation:edit")}</h3>
          <PermissionSwitch
            disabled={!data?.[code]?.includes("001") && view}
            isChecked={isPermissionChecked("003")}
            handleSwitchChange={handleEditSwitchChange}
          />
        </div>
      ) : (
        <div />
      )}
      {del ? (
        <div>
          <h3>{t("translation:delete")}</h3>
          <PermissionSwitch
            disabled={!data?.[code]?.includes("001") && view}
            isChecked={isPermissionChecked("004")}
            handleSwitchChange={handleDeleteSwitchChange}
          />
        </div>
      ) : (
        <div />
      )}
    </>
  );
}
